<template>
  <v-app :class="{ 'modern-theme': modernTheme, 'app-font': modernTheme }">
    <router-view></router-view>
    <theme-selection-dialog />
  </v-app>
</template>

<style>
.modern-theme.app-font,
.modern-theme.v-application {
  font-family: "Open Sans", sans-serif !important;
}

.modern-theme.theme--light.v-application {
  background: #f4f7f6 !important;
}

.modern-theme.theme--dark.v-application {
  background: #121212 !important;
}

.modern-theme .v-card:not(.v-card--flat) {
  border-radius: 16px !important;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.05) !important;
  border: none !important;
}

.modern-theme.theme--dark .v-card:not(.v-card--flat) {
  border-radius: 16px !important;
  background: #1e1e1e !important;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.35) !important;
  border: none !important;
}

.modern-theme .v-card.v-card--flat {
  border-radius: 16px !important;
  border: none !important;
}

.modern-theme.theme--dark .v-card.v-card--flat {
  border-radius: 16px !important;
  background: #1e1e1e !important;
  border: none !important;
}

.modern-theme .v-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-weight: 600 !important;
  transition: all 0.2s ease-in-out !important;
}

.modern-theme .v-btn:not(.v-btn--icon):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.modern-theme .v-btn:active {
  transform: scale(0.98);
}

.modern-theme.theme--light .v-text-field--outlined fieldset {
  border-radius: 10px !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.modern-theme.theme--dark .v-text-field--outlined fieldset {
  border-radius: 10px !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}

.modern-theme .v-list-item {
  border-radius: 8px !important;
}

.modern-theme.theme--light .v-navigation-drawer {
  border-right: none !important;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.02) !important;
}

.modern-theme.theme--dark .v-navigation-drawer {
  border-right: none !important;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.4) !important;
}

.modern-theme .v-btn-toggle .v-btn {
  border-radius: 0 !important;
}

.modern-theme .v-btn-toggle .v-btn:first-child {
  border-top-left-radius: 10px !important;
  border-bottom-left-radius: 10px !important;
}

.modern-theme .v-btn-toggle .v-btn:last-child {
  border-top-right-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
}

.modern-theme .v-chip {
  border-radius: 8px !important;
  font-weight: 600 !important;
}

/* Table Styling */
.modern-theme .v-data-table {
  background-color: transparent !important;
}

.modern-theme.theme--light .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  color: #9e9e9e !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.modern-theme.theme--dark .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  color: #b0bec5 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.modern-theme.theme--light .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 0.9rem !important;
  font-weight: 500 !important;
  color: #424242 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03) !important;
}

.modern-theme.theme--dark .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 0.9rem !important;
  font-weight: 500 !important;
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.modern-theme.theme--light
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(
    .v-data-table__empty-wrapper
  ) {
  background: rgba(0, 0, 0, 0.02) !important;
}

.modern-theme.theme--dark
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(
    .v-data-table__empty-wrapper
  ) {
  background: rgba(255, 255, 255, 0.04) !important;
}

.modern-theme .transparent-table {
  background: transparent !important;
}
</style>

<script>
import { routes } from "./router";
import RefreshButton from "@/components/RefreshButton";
import app, { getToken, onMessage } from "@/plugins/firebase.app";
import api from "@/api";
import { getMessaging, isSupported } from "firebase/messaging";
import { mapState } from "vuex";
import ThemeSelectionDialog from "@/components/ThemeSelectionDialog.vue";

let unsubscribePushOnMessage = null;
export default {
  name: "App",
  components: { ThemeSelectionDialog },

  data: () => {
    return {};
  },
  computed: {
    ...mapState(["modernTheme", "darkMode"]),
    routes: () => {
      return routes;
    },
  },
  watch: {
    darkMode: {
      immediate: true,
      handler(val) {
        this.$vuetify.theme.dark = !!val;
      },
    },
    modernTheme: {
      immediate: true,
      handler(val) {
        if (val) {
          // Modern Theme Colors
          this.$vuetify.theme.themes.light.primary = "#B71C1C";
          this.$vuetify.theme.themes.light.anchor = "#B71C1C";
          this.$vuetify.theme.themes.light.secondary = "#757575";
          this.$vuetify.theme.themes.light.error = "#FF5252";
          this.$vuetify.theme.themes.light.info = "#2196F3";
          this.$vuetify.theme.themes.light.success = "#4CAF50";
          this.$vuetify.theme.themes.light.warning = "#FFC107";
        } else {
          // Classic Theme Colors
          this.$vuetify.theme.themes.light.primary = "#6c0000";
          this.$vuetify.theme.themes.light.anchor = "#6c0000";
          this.$vuetify.theme.themes.light.secondary = "#8a8d93";
          this.$vuetify.theme.themes.light.error = "#ff4c51";
          this.$vuetify.theme.themes.light.info = "#16b1ff";
          this.$vuetify.theme.themes.light.success = "#56ca00";
          this.$vuetify.theme.themes.light.warning = "#ffb400";
        }
      },
    },
  },
  methods: {
    async fetchSwenv() {
      return await fetch("/swenv.js?_v=" + Date.now()).then((r) => r.text());
    },
    async checkForUpdates() {
      if (document.visibilityState === "visible") {
        console.info("[checkForUpdates] App visible. Loading Swenv.js");
        try {
          const swenv = await this.fetchSwenv();
          const savedSwenv = localStorage.getItem("swenv", swenv);
          if (!savedSwenv) {
            console.info(
              "[checkForUpdates] No saved swenv found. Setting it to " + swenv
            );
            localStorage.setItem("swenv", swenv);
            return;
          }
          if (savedSwenv !== swenv) {
            console.info("[checkForUpdates] Needs update. Notifying.");
            this.$toast("Päivitys saatavilla.", {
              closeButton: RefreshButton,
              toastClassName: "refresh--toast",
              timeout: false,
            });
            window.swenv = swenv;
          } else {
            console.info("[checkForUpdates] No need for update");
          }
        } catch (e) {
          console.error("[checkForUpdates] error", e);
        }
      } else {
        console.info("[checkForUpdates] App hidden");
      }
    },
  },
  mounted() {
    document.removeEventListener("visibilitychange", this.checkForUpdates);
    document.addEventListener("visibilitychange", this.checkForUpdates);
    if (unsubscribePushOnMessage) {
      try {
        unsubscribePushOnMessage();
      } catch (e) {
        console.error(e);
      }
    }
    try {
      if (this.$hasRole("user") || this.$hasRole("naisosasto")) {
        if ("Notification" in window) {
          if (!isSupported()) {
            console.error("Firebase messaging not supported");
            return;
          }
          const messaging = getMessaging(app);
          try {
            unsubscribePushOnMessage = onMessage(messaging, (payload) => {
              console.log("Message received. ", payload);
              this.$toast(
                <p>
                  <strong>{payload.data?.title}</strong>
                  <br />
                  {payload.data.body}
                </p>,
                {
                  type: "info",
                  timeout: 10000,
                }
              );
            });
            console.log("onMessage registered");
          } catch (e) {
            console.error("onMessage error", e);
          }
          (async () => {
            console.log("Notification.permission =", Notification.permission);
            let permission = Notification.permission;

            if (permission !== "granted") {
              const deniedStr = localStorage.getItem(
                "notificationPermissionDenied"
              );
              if (deniedStr) {
                try {
                  const deniedObj = JSON.parse(deniedStr);
                  const oneMonth = 30 * 24 * 60 * 60 * 1000;
                  if (
                    deniedObj &&
                    deniedObj.deniedAt &&
                    Date.now() - deniedObj.deniedAt < oneMonth
                  ) {
                    console.info(
                      "User skipped notification permission, waiting for monthly recheck."
                    );
                    return;
                  }
                  localStorage.removeItem("notificationPermissionDenied");
                } catch (e) {
                  console.error(e);
                }
              }
            }

            if (permission === "default") {
              const response = await this.$confirm(
                "Haluatko vastaanottaa ilmoituksia ilmoitustaululle tulevista ilmoituksista?",
                {
                  buttonTrueText: "Kyllä",
                  buttonFalseText: "Ei",
                  title: "Ilmoitukset",
                  color: "primary",
                  persistent: true,
                }
              );
              if (!response) {
                console.info("User declined notification prompt");
                localStorage.setItem(
                  "notificationPermissionDenied",
                  JSON.stringify({ deniedAt: Date.now() })
                );
                return false;
              }
              try {
                permission = await Notification.requestPermission();
              } catch (permErr) {
                console.error("requestPermission error:", permErr);
              }
            }

            if (permission === "granted") {
              try {
                console.log("Notification permission is granted. Syncing FCM token...");
                const serviceWorkerRegistration = await navigator.serviceWorker.ready;
                const token = await getToken(messaging, {
                  vapidKey:
                    "BCZ3S_HG6b-bUNUJkxygFPTu_UDHfthcEcFmCODFxCTVNNVNRbZtRhYWcNnK7DMkTWAfLEopWOynqpOb_pcJyi0",
                  serviceWorkerRegistration: serviceWorkerRegistration,
                });

                if (token) {
                  console.log("Firebase Messaging Token acquired:", token);
                  await api.firebaseMessagingAddToken({
                    token,
                    userAgent: navigator.userAgent,
                  });
                } else {
                  console.warn("No FCM token returned");
                }
              } catch (err) {
                console.error("Error acquiring or registering FCM token:", err);
              }
            } else {
              console.log("Notifications not granted:", permission);
            }
          })();
        }
      }
    } catch (e) {
      console.error("firebaseMessaging error", e);
    }
  },
  beforeDestroy() {
    if (unsubscribePushOnMessage) {
      unsubscribePushOnMessage();
    }
    document.removeEventListener("visibilitychange", this.checkForUpdates);
  },
};
</script>

<style lang="scss">
@import "./main";

.Vue-Toastification__container.top-right {
  top: 64px;
}

.nav-bar {
  background-color: var(--v-error-base);
}
</style>
